import { useMutation } from '@apollo/client';
import { Box, Flex, Stack, Text, useToast } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import z from 'zod';
import { passwordSchema } from '../../../utils/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChangeProfilePassword } from '../../../graphql/mutations/ChangeProfilePassword';
import ZaraButton from '../../custom-components/buttons/Button';
import { useError } from '../../../error-provider/ErrorProvider';
import FormInput from '../../custom-components/Inputs/FormInput';
import { INPUT_PROPS } from '../../../utils/constants';
import { useGetProfileMenu } from '../../../hooks';
import { PATHS } from '../../../router/routes';

const schema = z
    .object({
        currentPassword: passwordSchema,
        newPassword: passwordSchema,
        confirmPassword: passwordSchema,
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: "Passwords don't match",
        path: ['confirmPassword'],
    })
    .refine((data) => /(?=.*[A-Z])/.test(data.newPassword), {
        message: 'Password does not have uppercase letter.',
        path: ['confirmPassword'],
    })
    .refine((data) => /(?=.*[a-z])/.test(data.newPassword), {
        message: 'Password does not have lowercase letter.',
        path: ['confirmPassword'],
    })
    .refine((data) => /(?=.*\d)/.test(data.newPassword), {
        message: 'Password does not have digit.',
        path: ['confirmPassword'],
    })
    .refine((data) => /(?=.*[!@#$%^&*])/.test(data.newPassword), {
        message: 'Password does not have special character.',
        path: ['confirmPassword'],
    });

const ProfilePasswordComponent = () => {
    const router = useNavigate();
    const { showError } = useError();
    const toast = useToast();
    const [hideNewPass, setHideNewPass] = useState(true);
    const [hideCurrentPass, setHideCurrentPass] = useState(true);
    const [hideConfirmPass, setHideConfirmPass] = useState(true);

    const { control, handleSubmit, setValue } = useForm({
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });

    const [onChangeProfilePassword, { loading }] = useMutation(ChangeProfilePassword, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const location = useLocation();
    const { pathname } = location;

    const profileMenu = useGetProfileMenu();

    const checkCurrentPage = (page) => {
        if (pathname === page) {
            return true;
        } else {
            return false;
        }
    };

    function clearFields() {
        setValue('currentPassword', '');
        setValue('newPassword', '');
        setValue('confirmPassword', '');
    }

    async function onSubmit(data) {
        const variables = {
            currentPassword: data.currentPassword,
            newPassword1: data.newPassword,
            newPassword2: data.confirmPassword,
        };

        await onChangeProfilePassword({
            variables: variables,
            onCompleted(data) {
                if (data.changeProfilePassword.__typename === 'SuccessType') {
                    toast({
                        title: data.changeProfilePassword.message,
                        status: 'success',
                        isClosable: true,
                    });
                    clearFields();
                }
                if (data.changeProfilePassword.__typename === 'ErrorType') {
                    toast({
                        title: data.changeProfilePassword.message,
                        status: 'error',
                        isClosable: true,
                    });
                }
            },
        });
    }

    const inputs = [
        {
            label: INPUT_PROPS.CURRENT_PASSWORD.LABEL,
            controllName: INPUT_PROPS.CURRENT_PASSWORD.CONTROLL_NAME,
            placeholder: INPUT_PROPS.CURRENT_PASSWORD.PLACEHOLDER,
            hidePass: hideCurrentPass,
            setHidePass: setHideCurrentPass,
        },
        {
            label: INPUT_PROPS.NEW_PASSWORD.LABEL,
            controllName: INPUT_PROPS.NEW_PASSWORD.CONTROLL_NAME,
            placeholder: INPUT_PROPS.NEW_PASSWORD.PLACEHOLDER,
            hidePass: hideNewPass,
            setHidePass: setHideNewPass,
        },
        {
            label: INPUT_PROPS.CONFIRM_PASSWORD.LABEL,
            controllName: INPUT_PROPS.CONFIRM_PASSWORD.CONTROLL_NAME,
            placeholder: INPUT_PROPS.CONFIRM_PASSWORD.PLACEHOLDER,
            hidePass: hideConfirmPass,
            setHidePass: setHideConfirmPass,
        },
    ];

    return (
        <>
            <Flex
                minH='560px'
                boxShadow=' 0px 1px 3px 0px #0000001A'
                mt='6'
                w='100%'
                css={{
                    '@media only screen and (max-width: 920px)': {
                        marginLeft: '0px',
                    },
                    '@media only screen and (min-width: 500px)': {
                        display: 'flex',
                    },
                    '@media only screen and (max-width: 450px)': {
                        display: 'none',
                    },
                    '@media only screen and (max-height: 450px)': {
                        marginBottom: '100px',
                    },
                }}
                border='1px solid #E2E8F0'
                borderRadius='4px'
            >
                <Stack p='10px' spacing='10px' w='278px' borderRight='1px solid #E2E8F0'>
                    {profileMenu.map((menuItem) => (
                        <Box
                            onClick={() => {
                                router(menuItem.path);
                            }}
                            cursor='pointer'
                            css={{
                                ':hover': {
                                    background: checkCurrentPage(menuItem.path) ? '#F7FAFC' : 'white',
                                    border: checkCurrentPage(menuItem.path) ? '1px solid white' : '1px solid #E2E8F0',
                                },
                                ':active': {
                                    background: checkCurrentPage(menuItem.path) ? '#F7FAFC' : 'white',
                                },
                            }}
                            bg={checkCurrentPage(menuItem.path) ? '#F7FAFC' : 'white'}
                            textAlign='left'
                            p='6px 12px'
                            border='1px solid white'
                            key={menuItem.name}
                        >
                            <Text fontSize='16px' lineHeight='28px' fontWeight='400'>
                                {menuItem.name}
                            </Text>
                        </Box>
                    ))}
                </Stack>
                <Box h='100%' w='100%'>
                    <Box as='form' onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing='3' p='20px'>
                            {inputs.map((input) => (
                                <Box key={input}>
                                    <FormInput
                                        variant='password'
                                        label={input.label}
                                        control={control}
                                        controllName={input.controllName}
                                        setHidePass={input.setHidePass}
                                        hidePass={input.hidePass}
                                        placeholder={input.placeholder}
                                    />
                                </Box>
                            ))}

                            <Box textAlign='right'>
                                <ZaraButton isLoading={loading} isdisabled={loading} type='submit'>
                                    Save
                                </ZaraButton>
                            </Box>
                        </Stack>
                    </Box>
                </Box>
            </Flex>

            {/*Mobile version */}

            <Box
                css={{
                    '@media only screen and (max-width: 450px)': {
                        display: 'block',
                    },
                    '@media only screen and (min-width: 500px)': {
                        display: 'none',
                    },
                }}
                mt='70px'
                as='form'
                onSubmit={handleSubmit(onSubmit)}
            >
                <Stack spacing='3'>
                    {inputs.map((input) => (
                        <Box key={input}>
                            <FormInput
                                variant='password'
                                label={input.label}
                                control={control}
                                controllName={input.controllName}
                                setHidePass={input.setHidePass}
                                hidePass={input.hidePass}
                                placeholder={input.placeholder}
                            />
                        </Box>
                    ))}
                </Stack>

                <Box mt='12px'>
                    <ZaraButton mb='20px' w='100%' isLoading={loading} isDisabled={loading} type='submit'>
                        Save
                    </ZaraButton>
                    <ZaraButton variant='secondary' w='100%' onClick={() => router(PATHS.PROFILE_INFO)}>
                        Back
                    </ZaraButton>
                </Box>
            </Box>
        </>
    );
};

export default ProfilePasswordComponent;
